import type { ResumeData } from "../components/StudentResumeView";
import { resolveMediaUrl } from "./mediaUrl";

export type { ResumeData };

export interface ResumeStudentSource {
  student_id: string;
  student_name: string;
  email?: string;
  phone?: string;
  college?: string;
  branch?: string;
  cgpa?: number | string;
  profile_pic_url?: string;
  github?: string | null;
  leetcode?: string | null;
  hackerrank?: string | null;
  hobbies?: string;
  resume_objective?: string;
  primary_skills?: string[];
  subject_scores?: Record<string, number | string | null | undefined>;
  secondary_skills?: Array<{
    skill_name: string;
    level?: string;
    type?: string;
  }>;
  student_education_details?: Array<{
    degree?: string;
    institute?: string;
    branch?: string;
    cgpa?: number | string;
    yop?: number | string;
  }>;
  student_project_details?: Array<{
    type?: string;
    description?: string;
    link?: string;
  }>;
  student_primary_certificate_details?: Array<{ name?: string }>;
  student_secondary_certificate_details?: Array<{ name?: string }>;
}

const roundNum = (n: unknown): string =>
  n != null && Number.isFinite(Number(n)) ? String(Math.round(Number(n))) : "";

export function mapStudentToResumeData(student: ResumeStudentSource): ResumeData {
  let education =
    student.student_education_details && student.student_education_details.length > 0
      ? student.student_education_details.map((e) => ({
          degree: e.degree || "",
          institution: e.institute || "",
          branch: e.branch || "",
          scoreOrGpa: roundNum(e.cgpa),
          year: e.yop != null ? String(e.yop) : "",
        }))
      : [];

  // Fallback when API returns college/branch/cgpa but no education_details block
  if (!education.some((e) => e.institution) && (student.college || student.branch || student.cgpa != null)) {
    education = [
      {
        degree: "",
        institution: student.college || "",
        branch: student.branch || "",
        scoreOrGpa: roundNum(student.cgpa),
        year: "",
      },
    ];
  }

  return {
    name: student.student_name || "Student",
    profilePicUrl: student.profile_pic_url
      ? resolveMediaUrl(student.profile_pic_url)
      : student.profile_pic_url,
    // Email / phone intentionally omitted for PO resume preview & download
    objective: student.resume_objective?.trim() || undefined,
    github: student.github || undefined,
    leetcode: student.leetcode || undefined,
    hackerrank: student.hackerrank || undefined,
    primarySkills: student.primary_skills ?? [],
    subjectScores: student.subject_scores,
    skillsWithLevel:
      student.secondary_skills?.map((s) => ({
        skill: s.skill_name,
        expertise: s.level || "—",
        type: s.type,
      })) ?? [],
    skillsVerified: false,
    education,
    projects:
      student.student_project_details?.map((p) => ({
        name: p.type || "Project",
        description: p.description || "",
        link: p.link,
      })) ?? [],
    primaryCertificates:
      student.student_primary_certificate_details?.map((c) => c.name || "").filter(Boolean) ?? [],
    secondaryCertificates:
      student.student_secondary_certificate_details?.map((c) => c.name || "").filter(Boolean) ?? [],
    hobbies: student.hobbies
      ? student.hobbies
          .split(/[,;]/)
          .map((h) => h.trim())
          .filter(Boolean)
      : [],
  };
}


export function sanitizeResumeFileName(name: string, studentId: string): string {
  const stripUnsafe = (value: string) =>
    Array.from(value)
      .map((ch) => {
        const code = ch.charCodeAt(0);
        if (code < 32 || '<>:"/\\|?*'.includes(ch)) return "_";
        return ch;
      })
      .join("")
      .replace(/\s+/g, "_");

  const safeName = stripUnsafe(name || "student").slice(0, 80);
  const safeId = stripUnsafe(String(studentId || "id"));
  return `${safeName}_${safeId}.pdf`;
}
